<template>
    <Spinner v-if="displaySpinner" />
    <InfoMessage
        title="First understand, then build"
        storageKey="welcome-message-stek"
        :forceShow="!hasAnyContent"
        showToggle
    >
        <p>
            A Stek is the model of your business — the source of truth your team
            and AI agents work from. Use domains to group related products and
            processes, think of them as distinct businesses within your
            organization.
        </p>
        <p v-if="domains.length === 0">
            Kick things off by creating your first domain. It will act as the
            home for products and services you provide to the world.
        </p>
    </InfoMessage>
    <Page>
        <template #eyebrow>
            <Eyebrow icon="pi pi-sitemap">Stek</Eyebrow>
        </template>
        <template #title>
            <div class="title-section">
                <div class="logo-container" :class="{ 'editing': isEditing }">
                    <img
                        :src="logoDataUrl || PLACEHOLDER_LOGO"
                        class="stek-logo"
                        alt="Stek Logo"
                        @error="handleLogoError"
                    />
                    <div
                        v-if="isEditing"
                        class="logo-edit-overlay"
                        @click="triggerFileInput"
                        role="button"
                        tabindex="0"
                        @keydown.enter="triggerFileInput"
                        aria-label="Change logo"
                    >
                        <i class="pi pi-pencil"></i>
                        <input
                            ref="fileInput"
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            @change="onLogoChange"
                            class="hidden-input"
                        />
                    </div>
                </div>
                <div class="title-info">
                    <h1 class="page-title">{{ stekName }}</h1>
                    <div v-if="!isEditing && fqdn" class="fqdn">
                        {{ fqdn }}
                    </div>
                    <div v-if="isEditing" class="fqdn-edit">
                        <InputText
                            v-model="fqdn"
                            placeholder="Enter domain (e.g., example.com)"
                            aria-label="Fully Qualified Domain Name"
                            :class="{ 'p-invalid': fqdnError }"
                            @input="validateFqdn"
                        />
                        <small v-if="fqdnError" class="p-error">{{
                            fqdnError
                        }}</small>
                    </div>
                </div>
            </div>
        </template>
        <template #controls>
            <Button
                v-if="stek && canDeleteStek && isEditing"
                severity="danger"
                text
                @click="deleteStek"
                :label="`Delete ${stekName}`"
                icon="pi pi-trash"
                class="ml-2"
            />
            <LastChangedInfo
                v-if="stek?.identity"
                class="last-changed"
                :entityId="stek.identity"
                :entityType="EntityType.STEK"
            />
        </template>

        <Section class="description" editable ref="descriptionSectionRef">
            <template #title>Description</template>
            <TextAppArtefact
                v-if="description"
                v-model="description"
                :isEditing="isEditing"
            />
        </Section>

        <Section
            v-if="isEditing || mission?.content"
            class="mission"
            editable
            description="The fundamental reason your organisation exists and the impact it aims to create."
        >
            <template #title>Mission</template>
            <TextAppArtefact
                v-if="mission"
                v-model="mission"
                :isEditing="isEditing"
            />
        </Section>

        <Section
            v-if="isEditing || vision?.content"
            class="vision"
            editable
            description="Where you're headed — the long-term future state you're working towards."
        >
            <template #title>Vision</template>
            <TextAppArtefact
                v-if="vision"
                v-model="vision"
                :isEditing="isEditing"
            />
        </Section>

        <Section
            v-if="isEditing || coreValues?.content"
            class="core-values"
            editable
            description="The principles that guide how your organisation operates and makes decisions."
        >
            <template #title>Core Values</template>
            <TextAppArtefact
                v-if="coreValues"
                v-model="coreValues"
                :isEditing="isEditing"
            />
        </Section>

        <Section
            v-if="isEditing || specializations?.content"
            class="specializations"
            editable
            description="The areas where your organisation excels and differentiates itself from others."
        >
            <template #title>Specializations</template>
            <TextAppArtefact
                v-if="specializations"
                v-model="specializations"
                :isEditing="isEditing"
            />
        </Section>

        <Section
            v-if="isEditing || targetMarkets?.content"
            class="target-markets"
            editable
            description="The customer segments and industries your organisation is focused on serving."
        >
            <template #title>Target Markets</template>
            <TextAppArtefact
                v-if="targetMarkets"
                v-model="targetMarkets"
                :isEditing="isEditing"
            />
        </Section>

        <Section class="domains-section" editable ref="domainsSectionRef">
            <DomainsList :isEditing="isEditing" :domains="domains" />
        </Section>

        <Section editable>
            <Documents
                v-if="stek"
                entityType="stek"
                :entity="stek"
                :isEditing="isEditing"
                :artefacts="artefacts"
            />
        </Section>
    </Page>

    <HintPopover
        v-if="currentHintStep"
        :current-step="currentHintStep"
        :is-last-step="isLastHintStep"
        @next="nextHintStep"
        @dismiss-all="dismissAllHints"
    />
</template>

<script setup lang="ts">
import InputText from "primevue/inputtext";
import { useConfirm } from "primevue/useconfirm";
import { useDialog } from "primevue/usedialog";
import { useToast } from "primevue/usetoast";
import { computed, h, ref, watch } from "vue";
import { useRouter } from "vue-router";

import Documents from "@/components/Documents.vue";
import DomainsList from "@/components/Domains/DomainsList.vue";
import Eyebrow from "@/components/Eyebrow.vue";
import GeneratingProgressBar from "@/components/GeneratingProgressBar.vue";
import InfoMessage from "@/components/InfoMessage.vue";
import LastChangedInfo from "@/components/LastChangedInfo.vue";
import Page from "@/components/Page.vue";
import Section from "@/components/Section.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import GenerateStekDetailsDialog from "@/components/dialogs/GenerateStekDetailsDialog.vue";
import HintPopover from "@/components/hints/HintPopover.vue";
import { useEditManager, useEditStore } from "@/composables/edit-manager";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager, useStekStore } from "@/composables/stek-manager";
import { usePageHints } from "@/composables/use-page-hints";
import { useArtefactsManager } from "@/lib/artefacts/user";
import { useServices } from "@/lib/openapi";
import type { StekGeneratedDetails } from "@/lib/openapi/gen/types.gen";
import Spinner from "@/shared/Spinner.vue";
import { useDomainsManager } from "@/stores/domains-store";
import { EntityType } from "@/stores/entity-store";
import { useUserManager } from "@/stores/user-store";

// Placeholder logo for when logo load fails or is unavailable
const PLACEHOLDER_LOGO =
    "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz48IS0tIFVwbG9hZGVkIHRvOiBTVkcgUmVwbywgd3d3LnN2Z3JlcG8uY29tLCBHZW5lcmF0b3I6IFNWRyBSZXBvIE1peGVyIFRvb2xzIC0tPg0KPHN2ZyB3aWR0aD0iODAwcHgiIGhlaWdodD0iODAwcHgiIHZpZXdCb3g9IjAgMCAxMjAgMTIwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPg0KPHJlY3Qgd2lkdGg9IjEyMCIgaGVpZ2h0PSIxMjAiIGZpbGw9IiNFRkYxRjMiLz4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMzMuMjUwMyAzOC40ODE2QzMzLjI2MDMgMzcuMDQ3MiAzNC40MTk5IDM1Ljg4NjQgMzUuODU0MyAzNS44NzVIODMuMTQ2M0M4NC41ODQ4IDM1Ljg3NSA4NS43NTAzIDM3LjA0MzEgODUuNzUwMyAzOC40ODE2VjgwLjUxODRDODUuNzQwMyA4MS45NTI4IDg0LjU4MDcgODMuMTEzNiA4My4xNDYzIDgzLjEyNUgzNS44NTQzQzM0LjQxNTggODMuMTIzNiAzMy4yNTAzIDgxLjk1NyAzMy4yNTAzIDgwLjUxODRWMzguNDgxNlpNODAuNTAwNiA0MS4xMjUxSDM4LjUwMDZWNzcuODc1MUw2Mi44OTIxIDUzLjQ3ODNDNjMuOTE3MiA1Mi40NTM2IDY1LjU3ODggNTIuNDUzNiA2Ni42MDM5IDUzLjQ3ODNMODAuNTAwNiA2Ny40MDEzVjQxLjEyNTFaTTQzLjc1IDUxLjYyNDlDNDMuNzUgNTQuNTI0NCA0Ni4xMDA1IDU2Ljg3NDkgNDkgNTYuODc0OUM1MS44OTk1IDU2Ljg3NDkgNTQuMjUgNTQuNTI0NCA1NC4yNSA1MS42MjQ5QzU0LjI1IDQ4LjcyNTQgNTEuODk5NSA0Ni4zNzQ5IDQ5IDQ2LjM3NDlDNDYuMTAwNSA0Ni4zNzQ5IDQzLjc1IDQ4LjcyNTQgNDMuNzUgNTEuNjI0OVoiIGZpbGw9IiM2ODc3ODciLz4NCjwvc3ZnPg==";

const toast = useToast();
const { currentOrganizationPermissions } = useUserManager();

const descriptionSectionRef = ref<HTMLElement | null>(null);
const domainsSectionRef = ref<HTMLElement | null>(null);
const {
    currentStep: currentHintStep,
    isLastStep: isLastHintStep,
    nextStep: nextHintStep,
    dismissAll: dismissAllHints,
} = usePageHints([
    {
        id: "stek-description",
        targetRef: descriptionSectionRef,
        title: "Tell your story",
        content:
            "Share what your organisation stands for. Switch to Edit mode to start writing.",
    },
    {
        id: "stek-add-domain",
        targetRef: domainsSectionRef,
        title: "Build your first domain",
        content:
            "Domains are the building blocks of your stek — each one groups related products and processes like a distinct business unit.",
    },
]);
const { organization } = useOrganizationManager();
const {
    logo,
    logoDataUrl,
    fqdn,
    stek,
    isLoadingSteks,
    description,
    mission,
    vision,
    coreValues,
    specializations,
    targetMarkets,
} = useStekManager();
const { domains, isFetching } = useDomainsManager();
const { isEditing } = useEditManager();
const editStore = useEditStore();
const { artefacts } = useArtefactsManager();
const router = useRouter();
const dialog = useDialog();

const api = useServices({
    opts: { throwOnError: false },
    requires: { auth: true, organization: true, stek: true },
});

const fqdnError = ref("");
const fileInput = ref<HTMLInputElement | null>(null);
const { fetchLogo, remove } = useStekStore();

const canDeleteStek = computed(() => {
    return currentOrganizationPermissions.value?.can_delete_stek ?? false;
});

const validateFqdn = () => {
    const fqdnRegex =
        /^(?!:\/\/)(?:[a-zA-Z0-9-]+\.)*[a-zA-Z0-9][a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;
    if (!stek.value?.fqdn) {
        fqdnError.value = "";
    } else if (!fqdnRegex.test(stek.value.fqdn)) {
        fqdnError.value = "Please enter a valid domain name";
    } else {
        fqdnError.value = "";
    }
};

const triggerFileInput = () => {
    fileInput.value?.click();
};

watch(
    () => stek.value?.logo,
    async () => {
        if (stek.value) {
            await fetchLogo(stek.value.identity);
        }
    },
    { immediate: true }
);

const onLogoChange = async (event: Event) => {
    if (!stek.value || !logo.value) {
        toast.add({
            severity: "error",
            summary: "Upload failed",
            detail: "Try again later.",
        });
        return;
    }

    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    if (file.size > 2 * 1024 * 1024) {
        toast.add({
            severity: "error",
            summary: "Upload failed",
            detail: "File must be less than 2MB",
        });
        return;
    }

    const allowedTypes = ["image/png", "image/jpeg"];
    if (!allowedTypes.includes(file.type)) {
        toast.add({
            severity: "error",
            summary: "Invalid file type",
            detail: "Please upload a PNG or JPG file",
        });
        return;
    }

    logo.value.updateContent(file);

    if (!stek.value.logo) {
        // Set a logo so we can render the preview.
        stek.value.logo = {
            identity: stek.value.identity + "_logo",
            name: "logo",
            mimetype: file.type,
            preview: "",
        };
    }
    stek.value.logo.preview = await logo.value.getDataUrl();
    input.value = "";
};

const stekName = computed(() => stek.value?.name || "...");
const displaySpinner = computed(() => {
    return isFetching.value || isLoadingSteks.value;
});

const confirm = useConfirm();

const deleteStek = async () => {
    if (!stek.value) {
        return;
    }
    let message = `This will remove EVERYTHING inside ${stek.value.name}.`;
    message += `\n\nThis will remove ${domains.value.length} domains and their children.`;
    if (domains.value.length) {
        message +=
            "\n\n" + `Domains: ${domains.value.map((d) => d.name).join(",")}.`;
    }
    confirm.require({
        header: "Are you sure?",
        message: message,
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Delete",
            severity: "danger",
        },
        defaultFocus: "reject",
        accept: async () => {
            if (!stek.value || !organization.value) {
                return;
            }
            await remove({ showToast: true });
            router.push({
                name: "organizationHome",
                params: {
                    orgName: organization.value.name,
                },
            });
        },
    });
};

type SelectedSections = {
    description: boolean;
    mission: boolean;
    vision: boolean;
    coreValues: boolean;
    specializations: boolean;
    targetMarkets: boolean;
};

const openSourcesDialog = (): Promise<{
    sources: string[];
    selected: SelectedSections;
}> => {
    return new Promise((resolve, reject) => {
        dialog.open(GenerateStekDetailsDialog, {
            props: {
                header: "Generate Details from Sources",
                style: { maxWidth: "600px", width: "90%" },
                modal: true,
                maximizable: false,
                draggable: false,
                closable: true,
            },
            onClose: (result?: {
                data?: { sources: string[]; selected: SelectedSections };
            }) => {
                if (!result?.data) {
                    reject(new Error("User cancelled"));
                    return;
                }
                resolve(result.data);
            },
        });
    });
};

const openProgressDialog = () =>
    dialog.open(
        h(GeneratingProgressBar, {
            message: "Generating details, this may take a minute...",
        }),
        {
            props: {
                header: "Generate Details",
                style: { maxWidth: "600px", width: "80%" },
                modal: true,
                maximizable: false,
                draggable: false,
            },
        }
    );

/**
 * Functionality is currently disabled.
 */
const _generateStekAdditionalDetails = async () => {
    if (!stek.value) return;

    try {
        const { sources, selected } = await openSourcesDialog();

        if (!isEditing.value) {
            await editStore.startEditing();
        }

        const progressDialog = openProgressDialog();

        const { response, data } =
            await api.startStekStekMgtGenerateStekDetailsStartPost({
                body: {
                    stek_id: stek.value.identity,
                    sources: [...sources],
                },
            });

        if (!response.ok || !data) {
            throw new Error(
                `Failed to generate details: ${response.status} ${response.statusText}`
            );
        }

        const generatedDetails = data as StekGeneratedDetails & {
            description?: string | null;
        };

        if (
            selected.description &&
            generatedDetails.description &&
            description.value
        ) {
            await description.value.updateContent(generatedDetails.description);
        }

        if (selected.mission && generatedDetails.mission && mission.value) {
            await mission.value.updateContent(generatedDetails.mission);
        }
        if (selected.vision && generatedDetails.vision && vision.value) {
            await vision.value.updateContent(generatedDetails.vision);
        }
        if (
            selected.coreValues &&
            generatedDetails.core_values &&
            coreValues.value
        ) {
            await coreValues.value.updateContent(generatedDetails.core_values);
        }
        if (
            selected.specializations &&
            generatedDetails.specializations &&
            specializations.value
        ) {
            await specializations.value.updateContent(
                generatedDetails.specializations
            );
        }
        if (
            selected.targetMarkets &&
            generatedDetails.target_markets &&
            targetMarkets.value
        ) {
            await targetMarkets.value.updateContent(
                generatedDetails.target_markets
            );
        }

        toast.add({
            severity: "success",
            summary: "Success",
            detail: "Details generated successfully",
        });

        progressDialog?.close();
    } catch (error) {
        if (error instanceof Error && error.message === "User cancelled") {
            return;
        }
        toast.add({
            severity: "error",
            summary: "Could not generate stek details",
            detail: error instanceof Error ? error.message : "Unknown error",
        });
    }
};

const hasAnyContent = computed(() => {
    return Boolean(
        domains.value?.length > 0 ||
        description.value?.content ||
        mission.value?.content ||
        vision.value?.content ||
        coreValues.value?.content ||
        specializations.value?.content ||
        targetMarkets.value?.content ||
        artefacts.value?.length > 0
    );
});

// Handle logo loading errors
const handleLogoError = () => {
    console.warn("Stek logo failed to load, using placeholder");
    // Set to placeholder logo if the image fails to load
    logoDataUrl.value = PLACEHOLDER_LOGO;
};
</script>

<style scoped lang="scss">
.title-section {
    display: flex;
    align-items: center;
    gap: 1rem;

    .logo-container {
        position: relative;
        height: 4em;
        max-width: 16em;

        .stek-logo {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }

        &.editing {
            cursor: pointer;

            &:hover .logo-edit-overlay {
                opacity: 1;
            }
        }

        .logo-edit-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: rgba(0, 0, 0, 0.5);
            color: white;
            z-index: 1;
            opacity: 0;
            transition: opacity 0.2s;

            &:focus-visible {
                opacity: 1;
                outline: 2px solid var(--primary-color);
                outline-offset: 2px;
            }
        }

        .hidden-input {
            display: none;
        }
    }

    .title-info {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;

        .page-title {
            margin: 0;
            font-size: var(--text-xl);
        }

        .fqdn {
            color: var(--text-color-secondary);
            font-size: 0.9rem;
        }

        .fqdn-edit {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;

            .help-text {
                color: var(--text-color-secondary);
            }
        }
    }
}

.title-and-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
}

.domains-section {
    margin-top: 1rem;
}

.loading-message {
    color: var(--text-color-secondary);
    font-style: italic;
    padding: 1rem 0;
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
    .title-section {
        flex-direction: column;
        align-items: flex-start;

        .logo-container {
            margin-bottom: 1rem;
        }
    }

    .title-and-controls {
        flex-direction: column;
        align-items: flex-start;

        .controls {
            margin-top: 1rem;
            align-self: flex-end;
        }
    }
}
</style>
